import {
  useRestApiProvider,
  SubscriptionGetDetailedResponse,
  SubscriptionGetLiteResponse,
  PlanInfo,
  AddonInfo,
  PendingInvoice,
  getPendingInvoiceType,
} from "@jugl-web/rest-api";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { useTranslations, useToast } from "@jugl-web/utils";
import { Alert, PageLoaderFull } from "@jugl-web/ui-components";
import { AddAITeamMembers } from "@jugl-web/ui-components/web/AddAITeamMembers/AddAITeamMembers";
import { useChatBot } from "@jugl-web/domain-resources/chat-bot";
import { SelectedPlanInfo } from "@web-src/modules/cx/components/CompletePurchaseDialog/components/SummaryStep/types";
import { CancelPlanAlertContent } from "@web-src/modules/cx/components/CancelPlanAlertContent";
import { WorkspaceSubscriptionDrawer } from "@web-src/modules/cx/components/WorkspaceSubscriptionDrawer/WorkspaceSubscriptionDrawer";
import { CompletePurchaseDialog } from "@web-src/modules/cx/components/CompletePurchaseDialog";
import {
  UpdateCardV2Alert,
  UpdateCardV2AlertHandle,
} from "@web-src/modules/cx/components/UpdateCardV2Alert";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import { ANONYMOUS_SESSION_PLAN_INFO } from "@jugl-web/utils/storage";
import { useSessionStorage } from "@jugl-web/utils/hooks/useStorage";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { useShowAlert } from "@jugl-web/domain-resources/ui-components";
import { SubscriptionTags } from "@jugl-web/rest-api/subscription/tags";
import {
  SubscriptionV2Context,
  SubscriptionV2ContextValue,
  EntitySubscription,
  AddonLineItem,
} from "./SubscriptionV2Context";

export const SubscriptionV2Provider: FC<PropsWithChildren> = ({ children }) => {
  const { subscriptionInfo, entity } = useEntityProvider();
  const entityId = entity?.id;
  const { subscriptionV2Api } = useRestApiProvider();
  const { t } = useTranslations();
  const { show: showChatBot } = useChatBot();
  const showAlert = useShowAlert();

  const { isAnonymous } = useAnonymousSession();
  const dispatch = useDispatch();

  // Invalidate subscription cache when entity changes
  const previousEntityIdRef = useRef(entityId);
  useEffect(() => {
    if (
      entityId &&
      previousEntityIdRef.current &&
      entityId !== previousEntityIdRef.current
    ) {
      dispatch(
        subscriptionV2Api.util.invalidateTags([
          SubscriptionTags.subscriptionDetails,
          SubscriptionTags.plans,
          SubscriptionTags.addons,
          SubscriptionTags.invoices,
          SubscriptionTags.upcomingInvoices,
        ])
      );
    }
    previousEntityIdRef.current = entityId;
  }, [entityId, dispatch, subscriptionV2Api]);

  // Lazy query for manual polling in async functions
  const [fetchSubscription] = subscriptionV2Api.useLazyGetSubscriptionQuery();

  // Drawer state
  const [isSubscriptionDrawerOpen, setIsSubscriptionDrawerOpen] =
    useState(false);
  const [isPurchaseDialogOpen, setIsPurchaseDialogOpen] = useState(false);
  const [selectedPlanInfo, setSelectedPlanInfo] = useState<
    SelectedPlanInfo | undefined
  >();
  const selectPlanCallbackRef = useRef<
    ((planInfo: SelectedPlanInfo) => void) | null
  >(null);
  const [, setSelectedPlan] = useSessionStorage<SelectedPlanInfo | undefined>(
    ANONYMOUS_SESSION_PLAN_INFO,
    undefined
  );

  const {
    data: subscriptionData,
    refetch: refetchSubscription,
    isLoading: isSubscriptionLoading,
  } = subscriptionV2Api.useGetSubscriptionQuery(
    entityId && !isAnonymous && entity.type === "cx"
      ? { entityId, params: { mode: "detailed" } }
      : skipToken
  );

  const subscription =
    (subscriptionData as SubscriptionGetDetailedResponse | undefined)
      ?.entity_id === entityId
      ? (subscriptionData as SubscriptionGetDetailedResponse)
      : undefined;

  const { data: liteSubscriptionData } =
    subscriptionV2Api.useGetSubscriptionQuery(
      entityId && !isAnonymous && entity.type === "cx"
        ? { entityId, params: { mode: "lite" } }
        : skipToken,
      { pollingInterval: 60_000 }
    );

  const liteSubscription = liteSubscriptionData as
    | SubscriptionGetLiteResponse
    | undefined;

  const pendingInvoices = useMemo(
    () => liteSubscription?.pending_invoices ?? [],
    [liteSubscription?.pending_invoices]
  );
  const hasPaymentFailed = pendingInvoices.some(
    (i) => getPendingInvoiceType(i) === "plan"
  );

  const subscriptionStatus = subscription?.status ?? liteSubscription?.status;
  const isSubscriptionCanceled =
    subscriptionStatus === "canceled" ||
    subscriptionStatus === "incomplete_expired";

  const isSubscriptionPendingCancel =
    !!subscription?.cancel_at && !isSubscriptionCanceled;

  const { data: plansData, isLoading: isPlansLoading } =
    subscriptionV2Api.useListPlansQuery(
      entityId && entity.type === "cx"
        ? { entityId, params: { type: "cx" } }
        : skipToken
    );
  const plans = plansData?.data;

  const freePlan = useMemo(
    () => plansData?.data.find((plan) => plan.meta_data.idx === 0),
    [plansData?.data]
  );

  // Extract current plan ID from subscription line items
  const currentPlanId = useMemo(() => {
    const planLineItem = subscription?.details?.plan?.line_items?.find(
      (item) => item.info.kind === "plan"
    );
    return planLineItem?.info.kind === "plan"
      ? (planLineItem.info as PlanInfo).plan_id
      : undefined;
  }, [subscription]);

  const currentPlan = useMemo(
    () => plans?.find((plan) => plan.id === currentPlanId),
    [plans, currentPlanId]
  );

  const isFree = (currentPlan?.meta_data.idx ?? 0) === 0;

  const maxRollOverCredits =
    (liteSubscription?.entitlements?.["jugl::ai_credits"] ?? 0) * 2 || 0;

  const planDefaultCredits =
    currentPlan?.default_addons?.find((a) => a.id === "jugl::ai_credits")
      ?.credits ??
    freePlan?.default_addons?.find((a) => a.id === "jugl::ai_credits")
      ?.credits ??
    0;

  const isMonthly = useMemo(
    () =>
      subscription?.details?.plan?.line_items?.some(
        (item) => item.interval === "month"
      ) ?? false,
    [subscription]
  );

  // Build EntitySubscription from raw data
  const entitySubscription = useMemo<EntitySubscription | undefined>(() => {
    if (!subscription) return undefined;

    const lineItems = subscription.details?.plan?.line_items ?? [];

    const planLineItem = lineItems.find((item) => item.info.kind === "plan");
    const planInfo = planLineItem?.info as PlanInfo | undefined;

    const addonLineItems: Record<string, AddonLineItem> = {};
    let totalPrice = 0;

    lineItems.forEach((item) => {
      totalPrice += item.unit_amount || 0;
      if (item.info.kind === "addon") {
        const addonInfo = item.info as AddonInfo;
        addonLineItems[addonInfo.addon_name] = {
          addonName: addonInfo.addon_name,
          quantity: item.quantity,
          subscriptionItemId: item.subscription_item_id,
          priceId: item.price_id,
          unitAmount: item.unit_amount,
        };
      }
    });

    return {
      status: subscription.status,
      planName: planInfo?.name ?? "",
      planId: planInfo?.plan_id ?? "",
      billingInterval: (planLineItem?.interval === "year"
        ? "year"
        : "month") as "month" | "year",
      currentPeriodEnd: subscription.current_period_end,
      currentPeriodStart: subscription.current_period_start,
      totalPrice,
      addonLineItems,
      raw: subscription,
    };
  }, [subscription]);

  // Fetch supported addons for the current plan
  const { data: addonsData } = subscriptionV2Api.useListAddonsQuery(
    entityId && currentPlanId
      ? { entityId, params: { plan_id: currentPlanId } }
      : skipToken
  );

  const userCount = subscription?.entitlements?.["jugl::users"] ?? 0;

  const defaultUserCredits = useMemo(() => {
    const defaultAddon = currentPlan?.default_addons?.find(
      (a) => a.id === "jugl::users"
    );
    return defaultAddon?.credits ?? 0;
  }, [currentPlan]);

  const defaultAgentKnowledge = useMemo(() => {
    const defaultAddon =
      currentPlan?.default_addons?.find((a) => a.id === "jugl::knowledge") ??
      freePlan?.default_addons?.find((a) => a.id === "jugl::knowledge");
    return defaultAddon?.credits ?? 0;
  }, [currentPlan, freePlan]);

  const extraUserCount = Math.max(0, userCount - defaultUserCredits);

  const seatUsage = liteSubscription?.quota?.["jugl::users"]?.usage ?? 0;

  const isLoading = isSubscriptionLoading || isPlansLoading;

  // Card update alert
  const setupCardAlertRef = useRef<UpdateCardV2AlertHandle>(null);
  const hasPaymentMethod = !!subscription?.payment_method;

  const updateCardDetails = useCallback(async () => {
    if (!setupCardAlertRef.current) {
      return { result: "apiError" as const };
    }
    return setupCardAlertRef.current.start();
  }, []);

  // Seat adjustment
  const [isAdjustSeatsOpen, setIsAdjustSeatsOpen] = useState(false);
  const { toast } = useToast();
  const [adjustAddon, { isLoading: isAdjustLoading }] =
    subscriptionV2Api.useAdjustAddonMutation();

  const adjustSeats = useCallback(() => {
    if (isSubscriptionPendingCancel) {
      showChatBot();
      return;
    }
    setIsAdjustSeatsOpen(true);
  }, [isSubscriptionPendingCancel, showChatBot]);

  const seatAddonPrice = useMemo(() => {
    const usersAddon = addonsData?.data?.find((a) => a.id === "jugl::users");
    const matchingPrice = usersAddon?.prices[0];
    return matchingPrice
      ? { priceId: matchingPrice.id, amount: matchingPrice.unit_amount / 100 }
      : undefined;
  }, [addonsData]);

  // --- Payment failed alert (shared for plan upgrade) ---
  const showPaymentFailedAlert = useCallback(
    (pendingInvoice?: PendingInvoice) => {
      showAlert({
        title: "Payment Failed",
        content: pendingInvoice
          ? "Payment require additional action"
          : "Payment failed. Please check your card details and try again.",
        buttons: pendingInvoice
          ? [
              { text: "Close", color: "grey", role: "close" },
              {
                text: "Complete Payment",
                color: "primary",
                onClick: (_, { closeAlert }) => {
                  closeAlert();
                  window.open(pendingInvoice.invoice_url, "_blank");
                },
              },
            ]
          : [
              { text: "Close", color: "grey", role: "close" },
              {
                text: "Update Card",
                color: "primary",
                onClick: async (_, { closeAlert }) => {
                  closeAlert();
                  await updateCardDetails();
                },
              },
            ],
      });
    },
    [showAlert, updateCardDetails]
  );

  // --- Change plan with async polling ---
  const [changePlan] = subscriptionV2Api.useChangePlanMutation();
  const [isChangePlanInProgress, setIsChangePlanInProgress] = useState(false);

  const upgradeSubscriptionPlan = useCallback(
    async (params: {
      planId: string;
      priceId: string;
      planName: string;
      onSuccess: () => void;
      onFailure?: (pendingInvoice?: PendingInvoice) => void;
    }) => {
      if (!entityId) return;
      const handleFailure = params.onFailure ?? showPaymentFailedAlert;
      setIsChangePlanInProgress(true);
      try {
        await changePlan({
          entityId,
          data: { plan_id: params.planId, price_id: params.priceId },
        }).unwrap();

        // Wait 5s for backend to process payment
        await new Promise((r) => {
          setTimeout(r, 5000);
        });

        // Poll up to 6 times (10s apart = ~60s total)
        for (let attempt = 0; attempt < 6; attempt += 1) {
          // eslint-disable-next-line no-await-in-loop
          const lite = (await fetchSubscription({
            entityId,
            params: { mode: "lite" },
          }).unwrap()) as SubscriptionGetLiteResponse;

          // eslint-disable-next-line no-await-in-loop
          const detailed = (await fetchSubscription({
            entityId,
            params: { mode: "detailed" },
          }).unwrap()) as SubscriptionGetDetailedResponse;
          const planInvoice = lite.pending_invoices?.find(
            (i) => getPendingInvoiceType(i) === "plan"
          );
          if (planInvoice) {
            setIsChangePlanInProgress(false);
            handleFailure(planInvoice);
            return;
          }

          const planItem = detailed.details?.plan?.line_items?.find(
            (item) => item.info.kind === "plan"
          );
          if (
            planItem?.info.kind === "plan" &&
            (planItem.info as PlanInfo).plan_id === params.planId
          ) {
            setIsChangePlanInProgress(false);
            params.onSuccess();
            return;
          }

          if (attempt < 5) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => {
              setTimeout(r, 10000);
            });
          }
        }

        // Timeout — treat as failure
        setIsChangePlanInProgress(false);
        handleFailure();
      } catch (error) {
        setIsChangePlanInProgress(false);
        showPaymentFailedAlert();
        throw error;
      }
    },
    [entityId, changePlan, fetchSubscription, showPaymentFailedAlert]
  );

  // --- Addon purchase with async polling ---
  const [isAddonPurchaseInProgress, setIsAddonPurchaseInProgress] =
    useState(false);

  const handleBuyTeamMembers = useCallback(
    async (newQuantity: number) => {
      if (!entityId || !seatAddonPrice) return;
      try {
        // Capture user count before the change
        const usersBefore = subscription?.entitlements?.["jugl::users"] ?? 0;

        await adjustAddon({
          entityId,
          data: { price_id: seatAddonPrice.priceId, qty: newQuantity },
        }).unwrap();
        setIsAdjustSeatsOpen(false);
        setIsAddonPurchaseInProgress(true);

        // Wait 5s for backend to process payment
        await new Promise((r) => {
          setTimeout(r, 5000);
        });

        // Poll up to 6 times (10s apart = ~60s total)
        for (let attempt = 0; attempt < 6; attempt += 1) {
          // eslint-disable-next-line no-await-in-loop
          const lite = (await fetchSubscription({
            entityId,
            params: { mode: "lite" },
          }).unwrap()) as SubscriptionGetLiteResponse;

          // eslint-disable-next-line no-await-in-loop
          await fetchSubscription({
            entityId,
            params: { mode: "detailed" },
          }).unwrap();

          // Check for seat addon pending invoice → payment failed
          const seatInvoice = lite.pending_invoices?.find(
            (i) => getPendingInvoiceType(i) === "addon-seats"
          );
          if (seatInvoice) {
            setIsAddonPurchaseInProgress(false);
            toast(
              t({
                id: "cx-plans-page.payment-failed",
                defaultMessage:
                  "Payment failed. Please check your card details and try again.",
              }),
              { variant: "error" }
            );
            return;
          }

          // Check if user entitlement changed (increase or decrease) → success
          const usersAfter = lite.entitlements?.["jugl::users"] ?? 0;
          if (usersAfter !== usersBefore) {
            setIsAddonPurchaseInProgress(false);
            toast(
              t({
                id: "cx-plans-page.team-members-updated",
                defaultMessage: "Team members updated successfully",
              }),
              { variant: "success" }
            );
            return;
          }

          if (attempt < 5) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => {
              setTimeout(r, 10000);
            });
          }
        }

        // Timeout — show generic processing alert
        setIsAddonPurchaseInProgress(false);
        showAlert({
          title: t({
            id: "cx-plans-page.addon-processing-title",
            defaultMessage: "Payment Processing",
          }),
          content: t({
            id: "cx-plans-page.addon-processing-content",
            defaultMessage:
              "Payment processing is taking longer than expected. Please refresh the page later or contact support.",
          }),
          buttons: [
            {
              text: t({ id: "common.ok", defaultMessage: "OK" }),
              color: "primary",
              role: "close",
            },
          ],
        });
      } catch {
        setIsAddonPurchaseInProgress(false);
      }
    },
    [
      entityId,
      seatAddonPrice,
      adjustAddon,
      fetchSubscription,
      toast,
      t,
      subscription,
      showAlert,
    ]
  );

  // --- Credit topup with async polling ---
  const [topupNow] = subscriptionV2Api.useTopupNowMutation();
  const [isTopupInProgress, setIsTopupInProgress] = useState(false);

  const topupCredits = useCallback(
    async (params: {
      amountInCents: number;
      onSuccess: () => void;
      onOpenInvoice: (invoiceUrl: string) => void;
    }) => {
      if (!entityId) return;

      setIsTopupInProgress(true);
      try {
        // Capture total credits before topup
        const liteBefore = (await fetchSubscription({
          entityId,
          params: { mode: "lite" },
        }).unwrap()) as SubscriptionGetLiteResponse;
        const creditsBefore = liteBefore.quota?.["jugl::ai_credits"];
        const totalBefore =
          (creditsBefore?.allowed ?? 0) + (creditsBefore?.balance ?? 0);

        // Call topup API
        const result = await topupNow({
          entityId,
          data: { amount: params.amountInCents },
        }).unwrap();

        // If invoice needs manual payment, no polling
        if (result.status === "open") {
          setIsTopupInProgress(false);
          params.onOpenInvoice(result.invoice_url);
          return;
        }

        // status === "paid" — poll for credit application
        // Wait 5s for backend to process
        await new Promise((r) => {
          setTimeout(r, 5000);
        });

        // Poll up to 6 times (10s apart = ~60s total)
        for (let attempt = 0; attempt < 6; attempt += 1) {
          // eslint-disable-next-line no-await-in-loop
          const lite = (await fetchSubscription({
            entityId,
            params: { mode: "lite" },
          }).unwrap()) as SubscriptionGetLiteResponse;

          const creditsAfter = lite.quota?.["jugl::ai_credits"];
          const totalAfter =
            (creditsAfter?.allowed ?? 0) + (creditsAfter?.balance ?? 0);

          if (totalAfter > totalBefore) {
            setIsTopupInProgress(false);
            params.onSuccess();
            return;
          }

          if (attempt < 5) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => {
              setTimeout(r, 10000);
            });
          }
        }

        // Timeout — show informational alert
        setIsTopupInProgress(false);
        showAlert({
          title: t({
            id: "cx-plans-page.topup-processing-title",
            defaultMessage: "Payment Processing",
          }),
          content: t({
            id: "cx-plans-page.topup-processing-content",
            defaultMessage:
              "Payment processing is taking longer than expected. Please refresh the page later and go to Manage Subscription to check.",
          }),
          buttons: [
            {
              text: t({
                id: "common.ok",
                defaultMessage: "OK",
              }),
              color: "primary",
              role: "close",
            },
          ],
        });
      } catch (error) {
        setIsTopupInProgress(false);
        throw error;
      }
    },
    [entityId, topupNow, fetchSubscription, showAlert, t]
  );

  const [cancelSubscriptionMutation] =
    subscriptionV2Api.useCancelSubscriptionMutation();

  const [isExtraCreditsDialogRequested, setIsExtraCreditsDialogRequested] =
    useState(false);
  const requestExtraCreditsDialog = useCallback(
    () => setIsExtraCreditsDialogRequested(true),
    []
  );
  const clearExtraCreditsDialogRequest = useCallback(
    () => setIsExtraCreditsDialogRequested(false),
    []
  );

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  const cancelSubscription = useCallback(() => {
    setIsCancelDialogOpen(true);
  }, []);

  const [isCancelInProgress, setIsCancelInProgress] = useState(false);

  const handleCancelDowngrade = useCallback(
    async (feedback: string) => {
      setIsCancelDialogOpen(false);
      if (!entityId) return;
      setIsCancelInProgress(true);
      try {
        await cancelSubscriptionMutation({
          entityId,
          data: feedback ? { feedback } : { feedback: "no-feedback" },
        }).unwrap();
        await new Promise((r) => {
          setTimeout(r, 5000);
        });
        await refetchSubscription();
        toast(
          t({
            id: "cx-plans-page.subscription-canceled",
            defaultMessage: "Subscription canceled successfully",
          }),
          { variant: "success" }
        );
      } catch {
        // pass
      } finally {
        setIsCancelInProgress(false);
      }
    },
    [entityId, cancelSubscriptionMutation, refetchSubscription, toast, t]
  );

  const reactivateCurrentPlan = useCallback(() => {
    if (!currentPlan) return;
    const interval = isMonthly ? "month" : "year";
    const priceId = currentPlan.prices.find((p) => p.interval === interval)?.id;
    if (!priceId) return;

    const planName = currentPlan.name;
    const intervalLabel = isMonthly ? "Monthly" : "Yearly";

    showAlert({
      title: "Reactivate your plan?",
      content: (
        <div className="text-center text-dark-700">
          Are you sure you want to reactivate your{" "}
          <span className="bg-gradient-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
            {planName}
          </span>{" "}
          plan? Your subscription will continue on the current {intervalLabel}{" "}
          billing cycle.
        </div>
      ),
      buttons: [
        { text: "Cancel", color: "grey", role: "close" },
        {
          text: "Reactivate Plan",
          color: "primary",
          onClick: (_, { closeAlert }) => {
            closeAlert();
            upgradeSubscriptionPlan({
              planId: currentPlan.id,
              priceId,
              planName,
              onSuccess: () => {
                window.location.reload();
              },
            }).catch(() => {
              // Provider already surfaces payment-failed alert internally
            });
          },
        },
      ],
    });
  }, [currentPlan, isMonthly, showAlert, upgradeSubscriptionPlan]);

  // Drawer control methods
  const openSubscriptionDrawer = useCallback(
    (config?: { onSelectPlan?: (planInfo: SelectedPlanInfo) => void }) => {
      selectPlanCallbackRef.current = config?.onSelectPlan ?? null;
      setIsSubscriptionDrawerOpen(true);
    },
    []
  );

  const closeSubscriptionDrawer = useCallback(() => {
    setIsSubscriptionDrawerOpen(false);
    selectPlanCallbackRef.current = null;
  }, []);

  const openPurchaseDialog = useCallback((planInfo: SelectedPlanInfo) => {
    setSelectedPlanInfo(planInfo);
    setIsPurchaseDialogOpen(true);
  }, []);

  const closePurchaseDialog = useCallback(() => {
    setIsPurchaseDialogOpen(false);
    setSelectedPlanInfo(undefined);
    setSelectedPlan(undefined);
  }, [setSelectedPlan]);

  const handleSelectPlan = useCallback(
    (planInfo: SelectedPlanInfo) => {
      if (selectPlanCallbackRef.current) {
        selectPlanCallbackRef.current(planInfo);
      } else {
        setIsSubscriptionDrawerOpen(false);
        openPurchaseDialog(planInfo);
      }
    },
    [openPurchaseDialog]
  );

  const handleDowngrade = useCallback(
    (planInfo: SelectedPlanInfo) => {
      setIsSubscriptionDrawerOpen(false);
      openPurchaseDialog(planInfo);
    },
    [openPurchaseDialog]
  );

  const value: SubscriptionV2ContextValue = useMemo(
    () => ({
      subscription,
      liteSubscription,
      entitySubscription,
      plans,
      supportedAddons: addonsData?.data,
      currentPlan,
      currentPlanId,
      userCount,
      extraUserCount,
      seatUsage,
      pendingInvoices,
      hasPaymentFailed,
      isSubscriptionCanceled,
      isSubscriptionPendingCancel,
      defaultAgentKnowledge,
      isFree,
      isMonthly,
      isLoading,
      refetchSubscription,
      cancelSubscription,
      reactivateCurrentPlan,
      openSubscriptionDrawer,
      closeSubscriptionDrawer,
      openPurchaseDialog,
      closePurchaseDialog,
      updateCardDetails,
      hasPaymentMethod,
      adjustSeats,
      upgradeSubscriptionPlan,
      isChangePlanInProgress,
      isAddonPurchaseInProgress,
      isExtraCreditsDialogRequested,
      requestExtraCreditsDialog,
      clearExtraCreditsDialogRequest,
      topupCredits,
      isTopupInProgress,
      maxRollOverCredits,
      planDefaultCredits,
    }),
    [
      defaultAgentKnowledge,
      subscription,
      liteSubscription,
      entitySubscription,
      plans,
      addonsData,
      currentPlan,
      currentPlanId,
      userCount,
      extraUserCount,
      seatUsage,
      pendingInvoices,
      hasPaymentFailed,
      isSubscriptionCanceled,
      isSubscriptionPendingCancel,
      isFree,
      isMonthly,
      isLoading,
      refetchSubscription,
      cancelSubscription,
      reactivateCurrentPlan,
      openSubscriptionDrawer,
      closeSubscriptionDrawer,
      openPurchaseDialog,
      closePurchaseDialog,
      updateCardDetails,
      hasPaymentMethod,
      adjustSeats,
      upgradeSubscriptionPlan,
      isChangePlanInProgress,
      isAddonPurchaseInProgress,
      isExtraCreditsDialogRequested,
      requestExtraCreditsDialog,
      clearExtraCreditsDialogRequest,
      topupCredits,
      isTopupInProgress,
      maxRollOverCredits,
      planDefaultCredits,
    ]
  );

  return (
    <SubscriptionV2Context.Provider value={value}>
      {children}
      <PageLoaderFull
        isActive={
          isChangePlanInProgress ||
          isAddonPurchaseInProgress ||
          isCancelInProgress ||
          isTopupInProgress
        }
        isTransparent
      />
      {entity && subscriptionInfo && (
        <>
          <WorkspaceSubscriptionDrawer
            isOpen={isSubscriptionDrawerOpen}
            onClose={closeSubscriptionDrawer}
            currentPlanId={currentPlanId}
            currentInterval={isMonthly ? "month" : "year"}
            onDowngrade={handleDowngrade}
            onSelectPlan={handleSelectPlan}
          />
          <CompletePurchaseDialog
            isOpen={isPurchaseDialogOpen}
            planInfo={selectedPlanInfo}
            onClose={closePurchaseDialog}
          />
          <Alert
            isOpen={isCancelDialogOpen}
            onRequestClose={() => setIsCancelDialogOpen(false)}
            title="⚠️ Cancel Plan"
            contentContainerClassName="px-10 pt-10 pb-10"
            textAlignment="left"
            content={
              <CancelPlanAlertContent
                onCancel={() => setIsCancelDialogOpen(false)}
                onDowngrade={handleCancelDowngrade}
              />
            }
          />
          <UpdateCardV2Alert ref={setupCardAlertRef} entityId={entity.id} />
          <AddAITeamMembers
            isOpen={isAdjustSeatsOpen}
            onClose={() => setIsAdjustSeatsOpen(false)}
            currentQuantity={extraUserCount}
            minQuantity={Math.max(
              0,
              (entity?.userCount ?? 0) - defaultUserCredits
            )}
            pricePerMember={seatAddonPrice?.amount ?? 25}
            isLoading={isAdjustLoading}
            onBuy={handleBuyTeamMembers}
          />
        </>
      )}
    </SubscriptionV2Context.Provider>
  );
};
